import { useState } from 'react';
import { Star, Check, Bell, Zap } from 'lucide-react';
import { useApplication } from '../../../hooks/queries/useApplications';
import { useBenchOptIn } from '../../../hooks/mutations/useApplicationMutations';

interface CandidateBenchViewProps {
  applicationId: string;
  onOptOut: () => void;
}

export default function CandidateBenchView({ applicationId, onOptOut }: CandidateBenchViewProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [optInError, setOptInError] = useState<string | null>(null);

  const { data: application, isLoading, isError } = useApplication(applicationId);
  const benchOptIn = useBenchOptIn();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || !application) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-900 mb-2">Application Not Found</p>
          <p className="text-base text-gray-500">This link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  const handleOptIn = () => {
    setOptInError(null);
    benchOptIn.mutate(applicationId, {
      onSuccess: () => setShowSuccess(true),
      onError: () => setOptInError('Something went wrong. Please try again.'),
    });
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#0156FF]" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
            You're on the Priority List!
          </h1>
          <p className="text-base text-gray-600 mb-3">
            We'll reach out as soon as a <span className="font-semibold">{application.jobRole}</span> opening matches your availability.
            You won't need to apply again — we'll come to you.
          </p>
          <p className="text-sm text-gray-400 mt-6">
            You can close this page now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto pb-40 px-6">
        {/* Header */}
        <header className="pt-8 pb-6 text-center">
          <Star className="w-16 h-16 text-[#0156FF] mx-auto mb-4 fill-[#0156FF]" strokeWidth={2} />
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            You're on Our Radar, {application.name}!
          </h1>
          <p className="text-base text-gray-600">
            We were impressed — and we don't want to lose you.
          </p>
        </header>

        {/* What "Bench" Means Card */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
          <h2 className="text-base font-bold text-gray-900 mb-2">What does "priority list" mean?</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-5">
            Right now, we don't have a <span className="font-semibold">{application.jobRole}</span> opening that
            fits your schedule perfectly. Rather than lose a great candidate, we'd like to keep you at the
            top of the list so you're the first to know when the right slot opens up.
          </p>

          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-[#0156FF]" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Skip the line</p>
                <p className="text-xs text-gray-500">No need to reapply when a spot opens — we'll reach out directly.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-[#0156FF]" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Notified first</p>
                <p className="text-xs text-gray-500">You'll hear about openings before they're publicly posted.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-[#0156FF]" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Your choice</p>
                <p className="text-xs text-gray-500">You can opt out at any time — no pressure.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Action Area (Sticky Bottom) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 shadow-lg">
        {optInError && (
          <p className="text-sm text-red-600 text-center mb-3">{optInError}</p>
        )}
        {/* Primary Button - Opt In */}
        <button
          type="button"
          onClick={handleOptIn}
          disabled={benchOptIn.isPending}
          className="w-full h-14 rounded-full bg-[#0156FF] text-white text-lg font-bold hover:bg-blue-700 transition-colors mb-3 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {benchOptIn.isPending ? 'Saving...' : "Yes, keep me on the list"}
        </button>

        {/* Secondary Button - Opt Out */}
        <button
          type="button"
          onClick={onOptOut}
          disabled={benchOptIn.isPending}
          className="w-full h-12 rounded-full bg-white text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          No thanks, remove me
        </button>
      </div>
    </div>
  );
}
